import { createTheme, alpha, darken, lighten } from '@mui/material/styles';

export const DrawerWidth = 250;

export function createAppTheme(dynamicTheme) {
  const Primary = dynamicTheme?.primaryColor || '#ffae00';
  const Secondary = dynamicTheme?.secondaryColor || '#000000';
  const Font = dynamicTheme?.themeFont || "'Poppins', sans-serif";
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  return createTheme({
    palette: {
      mode: 'light',
      primary: { main: Primary },
      secondary: { main: Secondary },
    },
    custom: { isMobile },
     typography: {
        fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif', // global font        

        // You can also override specific variants:
        h1: { fontSize: '2.25rem', fontWeight: 700, color: darken(Secondary) },
        h2: { fontSize: '1.75rem', fontWeight: 600, color: darken(Secondary) },
        h3: { fontSize: '2rem', fontWeight: 700, color: darken(Secondary) },
        h4: { fontSize: '1.25rem', fontWeight: 700, color: darken(Secondary) },
        h5: { fontSize: '1rem', fontWeight: 700, color: darken(Secondary) },
      },
    components: {
      // ===== GLOBAL TABLE RULES (apply everywhere) =====
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderTop: `3px solid ${Primary}`, // subtle accent
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: Primary,
              color: darken(Secondary),
              fontWeight: 600,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${alpha(Primary, 0.2)}`,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: alpha(Primary, 0.06),
            },
            '&.Mui-selected, &.Mui-selected:hover': {
              backgroundColor: alpha(Primary, 0.12),
            },
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            backgroundColor: alpha(Primary, 1),
            color: darken(Secondary),
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 40,
          },
          indicator: {
            backgroundColor: Primary, // underline color
            height: 3,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            color: Primary, // default text color
            '&.Mui-selected': {
              color: darken(Secondary, 1) , // selected tab color
              fontWeight: 600,
            },
            '&:hover': {
              color: darken(Primary, 0.2),
            },
          },
        },
      },
      
      // ===== OTHER GLOBALS =====
      MuiButton: {
        defaultProps: { disableRipple: true, disableElevation: true,
          
          size: isMobile ? "small" : "large",
         },
        styleOverrides: {
          containedPrimary: { 
            backgroundColor: alpha(Primary, 1),            
            '&:hover': { backgroundColor: darken(Primary, 0.12) } },
          containedSecondary: { 
            color:alpha(Primary, 1),
            '&:hover': { backgroundColor: darken(Secondary, 0.12) } },    
            
            outlinedPrimary: {
              color: darken(Primary, 1), // ⬅️ text color black
              borderColor: alpha(Primary, 1), // optional (black outline)
              '&:hover': {
                borderColor: darken(Primary, 0.12),
                backgroundColor: alpha(Primary, 0.12),
              },
            },
            outlinedSecondary: {
              borderColor: alpha(Primary, 0.12),
              backgroundColor: darken(Primary, 0.12),
              '&:hover': {
                borderColor: darken(Primary, 0.12),
                backgroundColor: alpha(Primary, 0.12),
              },
            },
          sizeSmall: {
            height: 30,          // Custom height for small
            padding: '4px 10px',
            fontSize: '0.75rem',
            color: darken(Secondary),
          },
          sizeMedium: {
            height: 40,          // Custom height for medium
            padding: '6px 16px',
            fontSize: '0.875rem',
            color: darken(Secondary),
          },
          sizeLarge: {
            height: 56,          // Custom height for large
            padding: '8px 22px',
            fontSize: '14px',
            color: darken(Secondary),
          },
        },
      },
      MuiTooltip: {
        defaultProps: { arrow: true },
        styleOverrides: {
          tooltip: { background: Primary },
          arrow: { color: Primary },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            width: DrawerWidth,
            background: Primary,
            color: '#fff',
            borderRadius: '0px 100px 0px 0px',
            borderRight: `1px solid ${lighten(Primary, 0.12)}`,
          },
        },
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: lighten(Primary, 0.2) } },
      },
      MuiPopover: { defaultProps: { disableScrollLock: true } },
      MuiPopper: { defaultProps: { disableScrollLock: true } },
      // Keep AppBar color static unless you want it dynamic:
      // If you ever want AppBar to follow primary bg, uncomment below:
      // MuiAppBar: {
      //   styleOverrides: { root: { backgroundColor: Primary, color: '#fff' } },
      // },
    },
  });
}
